import DOMPurify from 'dompurify';
import { ErrorResponse } from '../user/types';

const validatePassword = (password: string): string[] => {
    const error = [];
    if (password.length === 0) {
        error.push('Password cannot be empty');
    }
    if (password.length < 8) {
        error.push('Password should be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
        error.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
        error.push('Password must contain at least one letter');
    }
    if (!/[0-9]/.test(password)) {
        error.push('Password must contain at least one number');
    }
    return error;
};

// Permissive "looks like an email" check (something@something.tld) for
// client-side UX. The API still does authoritative validation server-side.
const validateEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const log = (message:unknown) => console.log('LOG:', message);

const handleError = (error:unknown) => {
    if (typeof error === 'object' && error !== null && 'response' in error && 'data' in (error as ErrorResponse).response) {
        const serverError = error as ErrorResponse; // Perform a type assertion after doing a type guard check
        // Fall back to `error` for endpoints (e.g. login) that return the
        // reason there instead of `message`.
        return serverError.response.data.message || serverError.response.data.error || 'Error';
    }
    return false;
};

function makeParams<T extends object>(params: T) {
    return Object.entries(params)
        .flatMap(([key, value]) => {
            if (value === undefined || value === null) return [];
            if (Array.isArray(value)) {
                return value.map((v) => `${encodeURIComponent(`${key}[]`)}=${encodeURIComponent(String(v))}`);
            }
            return [`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`];
        })
        .join('&');
}

const makeUrl = <T extends object>(url: string, paramsObject: T) => {
    const params = makeParams(paramsObject);
    return `${url}${params.length > 0 ? `?${params}` : ''}`;
};

/**
 * Sanitize HTML content using DOMPurify, stripping all tags for plain text display.
 *
 * @param text - The text that may contain HTML
 * @returns Plain text with HTML safely stripped via DOMPurify
 */
const stripHtmlTags = (text: string | null | undefined): string => {
    if (!text) return '';
    return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] }).trim();
};

/**
 * Convert a string to title case (first letter of each word capitalized,
 * remaining letters lowercased).
 *
 * @param value - The string to transform (e.g. a person's name)
 * @returns The title-cased string, e.g. "saranporn thitakasikorn" -> "Saranporn Thitakasikorn"
 */
const toTitleCase = (value: string | null | undefined): string => {
    if (!value) return '';
    return value
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Serialized error structure from Redux Toolkit
 */
interface SerializedError {
    name?: string;
    message?: string;
    code?: string;
    stack?: string;
}

/**
 * Extract a user-friendly error message from various error types.
 *
 * Handles Redux Toolkit SerializedError, API errors, and plain strings.
 * Returns a generic message if the error structure is unrecognized.
 *
 * @param error - The error object from a rejected action or API call
 * @param fallbackMessage - Default message if error cannot be parsed
 * @returns A user-friendly error message string
 */
const getErrorMessage = (
    error: SerializedError | string | null | undefined,
    fallbackMessage = 'An unexpected error occurred',
): string => {
    if (!error) return fallbackMessage;

    if (typeof error === 'string') {
        return error;
    }

    if (typeof error === 'object') {
        // Check for common error message properties
        if ('message' in error && typeof error.message === 'string' && error.message) {
            return error.message;
        }
        if ('name' in error && typeof error.name === 'string' && error.name) {
            return error.name;
        }
    }

    return fallbackMessage;
};

export {
    validatePassword,
    validateEmail,
    log,
    handleError,
    makeUrl,
    stripHtmlTags,
    getErrorMessage,
    toTitleCase,
};
